import { api } from './api';

export type HiringProgressStep = 1 | 2 | 3 | 4 | 5;

export interface HiringSignerProgress {
  id: string;
  name?: string;
  signed: boolean;
  signed_at?: string;
  signature_base64?: string;
}

export interface HiringSignatureProgress {
  accepted_terms: boolean;
  signers: HiringSignerProgress[];
  completed: boolean;
  completed_at?: string;
}

export interface HiringPaymentProgress {
  status: 'pending' | 'checkout_created' | 'paid' | 'completed';
  checkout_url?: string;
  session_id?: string;
  payment_intent_id?: string;
  updated_at?: string;
}

export interface HiringWorkflowProgress {
  hiring_code: string;
  step?: HiringProgressStep;
  signatures?: HiringSignatureProgress;
  payment?: HiringPaymentProgress;
  updated_at?: string;
}

interface HiringWorkflowProgressResponse {
  progress?: HiringWorkflowProgress;
}

const STORAGE_PREFIX = 'hiring_workflow_progress_';

const normalizeStep = (raw?: number): HiringProgressStep | undefined => {
  if (raw === 1 || raw === 2 || raw === 3 || raw === 4 || raw === 5) {
    return raw;
  }
  return undefined;
};

function readLocalProgress(code: string): HiringWorkflowProgress | null {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${code}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as HiringWorkflowProgress;
    if (!parsed || parsed.hiring_code !== code) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeLocalProgress(progress: HiringWorkflowProgress): void {
  try {
    localStorage.setItem(
      `${STORAGE_PREFIX}${progress.hiring_code}`,
      JSON.stringify(progress)
    );
  } catch {
    // Si el navegador bloquea storage o no hay espacio, mantenemos el flujo.
  }
}

/**
 * Combina las firmas preservando el `signature_base64` local cuando el patch
 * (p. ej. la respuesta del backend de workflow-progress) no lo trae.
 *
 * Bug previo: tras el redirect de Stripe, getProgress() mergeaba la respuesta
 * remota (sin base64) sobre la local y, al usar `patch.signatures ?? base`,
 * reemplazaba el objeto entero — borrando las firmas base64 ya capturadas. El
 * PDF definitivo se regeneraba sin firmas incrustadas.
 */
function mergeSignatures(
  base?: HiringSignatureProgress,
  patch?: HiringSignatureProgress
): HiringSignatureProgress | undefined {
  if (!patch) return base;
  if (!base) return patch;

  const byId = new Map<string, HiringSignerProgress>();
  for (const s of base.signers ?? []) {
    byId.set(s.id, { ...s });
  }
  for (const s of patch.signers ?? []) {
    const prev = byId.get(s.id);
    byId.set(s.id, {
      ...prev,
      ...s,
      // Preservar la firma base64 local si el patch no la incluye.
      signature_base64: s.signature_base64 ?? prev?.signature_base64,
      name: s.name ?? prev?.name,
      signed: s.signed || prev?.signed || Boolean(s.signature_base64 ?? prev?.signature_base64),
    });
  }

  return {
    ...base,
    ...patch,
    signers: Array.from(byId.values()),
  };
}

function mergeProgress(
  base: HiringWorkflowProgress | null,
  patch: Partial<HiringWorkflowProgress>,
  code: string
): HiringWorkflowProgress {
  const merged: HiringWorkflowProgress = {
    hiring_code: code,
    ...base,
    ...patch,
    signatures: mergeSignatures(base?.signatures, patch.signatures),
    payment: patch.payment ?? base?.payment,
    step: normalizeStep(patch.step ?? base?.step),
    updated_at: new Date().toISOString(),
  };
  return merged;
}

/**
 * Recupera las firmas manuscritas base64 desde localStorage con fallback al backend.
 * Resuelve el bug donde la firma se perdía tras redirect de Stripe (localStorage vacío).
 */
async function getHandwrittenSignatures(
  code: string
): Promise<Record<string, string> | undefined> {
  // 1. Intentar localStorage (fuente principal)
  try {
    const raw = localStorage.getItem(`contract_signatures_${code}`);
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, string>;
      if (parsed && Object.keys(parsed).length > 0) {
        console.log('✅ Firmas manuscritas cargadas desde localStorage:', Object.keys(parsed).length);
        return parsed;
      }
    }
  } catch {
    console.warn('⚠️ Error leyendo firmas de localStorage');
  }

  // 2. Fallback: workflow progress local (hiring_workflow_progress_{code})
  const localProgress = readLocalProgress(code);
  const localSigners = localProgress?.signatures?.signers;
  if (localSigners?.length) {
    const fromLocal: Record<string, string> = {};
    for (const s of localSigners) {
      if (s.signature_base64) fromLocal[s.id] = s.signature_base64;
    }
    if (Object.keys(fromLocal).length > 0) {
      console.log('✅ Firmas manuscritas recuperadas desde workflow progress local:', Object.keys(fromLocal).length);
      // Re-persistir en contract_signatures para futuros usos
      try {
        localStorage.setItem(`contract_signatures_${code}`, JSON.stringify(fromLocal));
      } catch { /* noop */ }
      return fromLocal;
    }
  }

  // 3. Fallback: backend API
  try {
    const { data } = await api.get<HiringWorkflowProgress | HiringWorkflowProgressResponse>(
      `/hiring/${code}/workflow-progress`
    );
    const remote =
      (data as HiringWorkflowProgressResponse)?.progress ??
      (data as HiringWorkflowProgress);
    const signers = remote?.signatures?.signers;
    if (signers?.length) {
      const fromBackend: Record<string, string> = {};
      for (const s of signers) {
        if (s.signature_base64) fromBackend[s.id] = s.signature_base64;
      }
      if (Object.keys(fromBackend).length > 0) {
        console.log('✅ Firmas manuscritas recuperadas desde backend:', Object.keys(fromBackend).length);
        // Re-persistir en localStorage para futuros usos
        try {
          localStorage.setItem(`contract_signatures_${code}`, JSON.stringify(fromBackend));
        } catch { /* noop */ }
        return fromBackend;
      }
    }
  } catch {
    console.warn('⚠️ No se pudieron recuperar firmas desde backend');
  }

  console.warn('⚠️ No se encontraron firmas manuscritas en ninguna fuente');
  return undefined;
}

export const hiringProgressService = {
  async getProgress(code: string): Promise<HiringWorkflowProgress | null> {
    const local = readLocalProgress(code);
    try {
      const { data } = await api.get<HiringWorkflowProgress | HiringWorkflowProgressResponse>(
        `/hiring/${code}/workflow-progress`
      );
      const remote =
        (data as HiringWorkflowProgressResponse)?.progress ??
        (data as HiringWorkflowProgress);
      if (remote?.hiring_code) {
        const normalized = mergeProgress(local, remote, code);
        writeLocalProgress(normalized);
        return normalized;
      }
      return local;
    } catch {
      return local;
    }
  },

  getHandwrittenSignatures,

  async saveProgress(
    code: string,
    patch: Partial<HiringWorkflowProgress>
  ): Promise<HiringWorkflowProgress> {
    const local = readLocalProgress(code);
    const merged = mergeProgress(local, patch, code);
    writeLocalProgress(merged);

    try {
      await api.post(`/hiring/${code}/workflow-progress`, merged);
    } catch {
      // Fallback silencioso: se mantiene la persistencia local si backend aún no está desplegado.
    }

    return merged;
  },
};

